import React, { useCallback } from 'react';
import * as styles from './styles/styles.module.scss';
import PropTypes from 'prop-types';
import OutlinedTextField from '../textFields/OutlinedTextField';
import errorIcon from '../../../../assets/images/shared/download-failed-new@3x.png';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { formatBytes, calculateImageDimensions } from '../../../utils/upload_helpers';

function ThumbnailEditor({
  url,
  caption,
  orientation,
  handleCaptionChange,
  file,
  idx,
  handleFileDelete,
  handleThumbnailRotation,
  lastThumbnail,
  uploading
}) {
  const memoHandleFileDelete = useCallback(() => {
    handleFileDelete(file);
  }, [file]);

  const memoHandleCaptionChange = useCallback(
    e => {
      handleCaptionChange(e, idx);
    },
    [idx]
  );

  const memoHandleThumbnailRotation = useCallback(
    direction => {
      return () => {
        handleThumbnailRotation(idx, direction);
      };
    },
    [idx, orientation]
  );

  const { imgWidth, imgHeight } = calculateImageDimensions(file);
  const removeButton = (
    <button disabled={uploading} className={`${styles.controlBtn} ${styles.removeBtn}`} type="button" onClick={memoHandleFileDelete}>
      {StringConstantsMixin.strings.CONVERTER.REMOVE_FILE}
    </button>
  );

  if (file.hasError) {
    return (
      <div className={`${styles.container} ${styles.errorContainer} ${lastThumbnail ? styles.lastContainer : ''}`}>
        <div className={styles.thumbnailContainer}>
          <img src={errorIcon} alt="error" className={styles.errorIcon} />
        </div>
        <div className={styles.toggleControls}>
          <div className={styles.uploadFailed}>{StringConstantsMixin.strings.CONVERTER.UPLOAD_FAILED}</div>
          <div className={styles.tryAgain}>{StringConstantsMixin.strings.CONTRIBUTE.TRY_AGAIN}</div>
          {removeButton}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${lastThumbnail ? styles.lastContainer : ''}`}>
      <div className={styles.thumbnailContainer}>
        <img
          src={url}
          alt="thumbnail"
          style={{ transform: `rotate(${orientation}deg)`, width: imgWidth, height: imgHeight }}
        />
      </div>
      <div className={styles.toggleControls}>
        <OutlinedTextField
          name="title"
          value={caption}
          className={styles.textField}
          label={StringConstantsMixin.strings.CAPTION}
          changeHandler={memoHandleCaptionChange}
          isDisabled={uploading}
        />
        <div className={styles.buttonsContainer}>
          <div className={styles.rotateButtons}>
            <button disabled={uploading} className={styles.controlBtn} type="button" onClick={memoHandleThumbnailRotation(-90)}>
              {StringConstantsMixin.strings.TRACK_DETAIL.PHOTO_UPLOAD_ROTATE_LABELS.LEFT}
            </button>
            |
            <button disabled={uploading} className={styles.controlBtn} type="button" onClick={memoHandleThumbnailRotation(90)}>
              {StringConstantsMixin.strings.TRACK_DETAIL.PHOTO_UPLOAD_ROTATE_LABELS.RIGHT}
            </button>
          </div>
          {removeButton}
        </div>
        <div className={styles.filesize}>{formatBytes(file.size)}</div>
      </div>
    </div>
  );
}

ThumbnailEditor.propTypes = {
  url: PropTypes.string,
  caption: PropTypes.string,
  orientation: PropTypes.number,
  file: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
    hasError: PropTypes.bool,
    size: PropTypes.number
  }),
  idx: PropTypes.number,
  lastThumbnail: PropTypes.bool,
  handleFileDelete: PropTypes.func,
  handleThumbnailRotation: PropTypes.func,
  handleCaptionChange: PropTypes.func
};

ThumbnailEditor.defaultProps = {
  url: '',
  caption: '',
  orientation: 0,
  file: {
    width: 0,
    height: 0,
    hasError: false,
    size: 0
  },
  idx: null,
  lastThumbnail: false,
  handleCaptionChange: () => {},
  handleFileDelete: () => {},
  handleThumbnailRotation: () => {}
};

export default ThumbnailEditor;
